import React, { useEffect, useState } from 'react';
import { APIURL } from '../../configApi/apiConfig';
import axios from '../../configApi/axiosConfig';

const nuevoFormulario = {
	Periodo: '',
	Cartera: '',
	Desde: '',
	Hasta: '',
	Cuota_Cero: false
};

const convertirMesAUltimoDia = (periodoMes) => {
	const [anioTexto, mesTexto] = String(periodoMes || '').split('-');
	const anio = Number(anioTexto);
	const mes = Number(mesTexto);

	if (!anio || !mes || mes < 1 || mes > 12) {
		return periodoMes;
	}

	const ultimoDia = new Date(anio, mes, 0).getDate();
	const diaFormateado = String(ultimoDia).padStart(2, '0');
	return `${anioTexto}-${mesTexto}-${diaFormateado}`;
};

const esVerdadero = (valor) => valor === true || valor === 1 || valor === '1';

const normalizarRespuestaCartera = (respuesta) => {
	if (Array.isArray(respuesta)) return respuesta;
	if (Array.isArray(respuesta?.data)) return respuesta.data;
	if (respuesta?.data && typeof respuesta.data === 'object') return [respuesta.data];
	if (respuesta && typeof respuesta === 'object') return [respuesta];
	return [];
};

const mapearRegistro = (item) => ({
	...item,
	Cuota_Cero: esVerdadero(item?.Cuota_Cero)
});

export function NewtipoCartera() {
	const [registros, setRegistros] = useState([]);
	const [periodoSeleccionado, setPeriodoSeleccionado] = useState('');
	const [formData, setFormData] = useState(nuevoFormulario);
	const [error, setError] = useState('');
	const [errorBusqueda, setErrorBusqueda] = useState('');
	const [loadingCartera, setLoadingCartera] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [confirmDeleteId, setConfirmDeleteId] = useState(null);
	const [toast, setToast] = useState({ show: false, type: 'success', message: '' });

	useEffect(() => {
		if (!toast.show) {
			return undefined;
		}

		const timer = setTimeout(() => {
			setToast((prev) => ({ ...prev, show: false }));
		}, 3500);

		return () => clearTimeout(timer);
	}, [toast.show]);

	const mostrarToast = (type, message) => {
		setToast({ show: true, type, message });
	};

	const fetchCarteraData = async (periodoMes) => {
		try {
			setLoadingCartera(true);
			const periodoApi = convertirMesAUltimoDia(periodoMes);
			const response = await axios.get(APIURL.cbo_tipoCarteraFindAll(periodoApi));
			const data = normalizarRespuestaCartera(response.data).map(mapearRegistro);
			setRegistros(data);
		} catch (fetchError) {
			console.error('Error al cargar carteras:', fetchError);
			setRegistros([]);
			setErrorBusqueda('No se pudo cargar la información para ese período.');
			mostrarToast('error', 'No se pudo cargar la información.');
		} finally {
			setLoadingCartera(false);
		}
	};

	const handlePeriodoChange = (event) => {
		setPeriodoSeleccionado(event.target.value);
		setErrorBusqueda('');
	};

	const buscarPorPeriodo = async () => {
		if (!periodoSeleccionado) {
			setErrorBusqueda('Debes seleccionar un período para buscar.');
			mostrarToast('error', 'Selecciona un período para buscar.');
			return;
		}

		setErrorBusqueda('');
		await fetchCarteraData(periodoSeleccionado);
	};

	const manejarCambio = (event) => {
		const { name, value, type, checked } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value
		}));
	};

	const limpiarFormulario = () => {
		setFormData(nuevoFormulario);
		setEditingId(null);
		setError('');
	};

	const validarFormulario = () => {
		if (!formData.Periodo || !formData.Cartera || formData.Desde === '' || formData.Hasta === '') {
			setError('Completa los campos obligatorios: Periodo, Cartera, Desde y Hasta.');
			return false;
		}

		const desdeNumero = Number(formData.Desde);
		const hastaNumero = Number(formData.Hasta);

		if (hastaNumero < desdeNumero) {
			setError('El campo Hasta debe ser mayor o igual a Desde.');
			return false;
		}

		return true;
	};

	const guardarRegistro = async (event) => {
		event.preventDefault();
		setError('');

		if (!validarFormulario()) {
			return;
		}

		const payload = {
			Periodo: convertirMesAUltimoDia(formData.Periodo),
			Cartera: formData.Cartera.trim().toUpperCase(),
			Desde: Number(formData.Desde),
			Hasta: Number(formData.Hasta),
			Cuota_Cero: Boolean(formData.Cuota_Cero)
		};

		try {
			setSaving(true);

			if (editingId) {
				await axios.patch(APIURL.patch_cbo_tipocartera(editingId), payload);
				mostrarToast('success', 'Registro actualizado correctamente.');
			} else {
				await axios.post(APIURL.post_cbo_tipocartera(), payload);
				mostrarToast('success', 'Registro creado correctamente.');
			}

			const periodoConsulta = formData.Periodo || periodoSeleccionado;
			if (periodoConsulta) {
				setPeriodoSeleccionado(periodoConsulta);
				await fetchCarteraData(periodoConsulta);
			}

			limpiarFormulario();
		} catch (saveError) {
			console.error('Error al guardar registro:', saveError);
			setError('Hubo un error al guardar el registro. Intenta nuevamente.');
			mostrarToast('error', 'No se pudo guardar el registro.');
		} finally {
			setSaving(false);
		}
	};

	const cargarParaEditar = (item) => {
		setEditingId(item.sCbo_TipoCartera);
		setFormData({
			Periodo: String(item.Periodo || '').substring(0, 7),
			Cartera: item.Cartera || '',
			Desde: item.Desde ?? '',
			Hasta: item.Hasta ?? '',
			Cuota_Cero: esVerdadero(item.Cuota_Cero)
		});
		setError('');
	};

	const eliminarRegistro = async (sCbo_TipoCartera) => {
		try {
			await axios.delete(APIURL.delete_cbo_tipocartera(sCbo_TipoCartera));
			setRegistros((prev) => prev.filter((item) => item.sCbo_TipoCartera !== sCbo_TipoCartera));

			if (periodoSeleccionado) {
				await fetchCarteraData(periodoSeleccionado);
			}

			mostrarToast('success', 'Registro eliminado correctamente.');
		} catch (deleteError) {
			console.error('Error al eliminar registro:', deleteError);
			setError('Hubo un error al eliminar el registro.');
			mostrarToast('error', 'No se pudo eliminar el registro.');
		}
	};

	const confirmarEliminacion = async () => {
		if (!confirmDeleteId) {
			return;
		}

		const id = confirmDeleteId;
		setConfirmDeleteId(null);
		await eliminarRegistro(id);
	};

	const registroAEliminar = registros.find((item) => item.sCbo_TipoCartera === confirmDeleteId);

	return (
		<div className="p-4 md:p-6 bg-gray-50 min-h-screen">
			<div className="max-w-7xl mx-auto space-y-5">
				{toast.show && (
					<div className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg text-sm text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
						{toast.message}
					</div>
				)}

				<div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
					<h2 className="text-2xl font-bold text-primaryBlue">Mantenimiento Tipo Cartera</h2>
					<p className="text-sm text-gray-500 mt-1">Consulta por período, crea nuevos registros y edita los existentes.</p>
				</div>

				<form onSubmit={guardarRegistro} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
					<h3 className="text-lg font-semibold text-gray-800 mb-4">{editingId ? 'Editar registro' : 'Agregar registro'}</h3>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Periodo</label>
							<input
								type="month"
								name="Periodo"
								value={formData.Periodo}
								onChange={manejarCambio}
								className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primaryBlue"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Cartera</label>
							<input
								type="text"
								name="Cartera"
								value={formData.Cartera}
								onChange={manejarCambio}
								className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primaryBlue uppercase"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
							<input
								type="number"
								name="Desde"
								value={formData.Desde}
								onChange={manejarCambio}
								className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primaryBlue"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
							<input
								type="number"
								name="Hasta"
								value={formData.Hasta}
								onChange={manejarCambio}
								className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primaryBlue"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Cuota Cero</label>
							<label className="flex items-center gap-2 h-[42px] px-3 rounded-xl border border-gray-300 text-sm text-gray-700 bg-white">
								<input
									type="checkbox"
									name="Cuota_Cero"
									checked={Boolean(formData.Cuota_Cero)}
									onChange={manejarCambio}
									className="h-4 w-4"
								/>
								<span>Aplica cuota cero</span>
							</label>
						</div>
					</div>

					{error && <p className="text-red-600 text-sm mt-3">{error}</p>}

					<div className="flex items-center gap-2 mt-5">
						<button
							type="submit"
							disabled={saving}
							className="bg-primaryBlue text-white px-4 py-2 rounded-xl text-sm hover:opacity-90 disabled:opacity-50"
						>
							{saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Agregar'}
						</button>
						<button
							type="button"
							onClick={limpiarFormulario}
							className="border border-gray-300 text-gray-700 px-4 py-2 rounded-xl text-sm hover:bg-gray-50"
						>
							{editingId ? 'Cancelar edición' : 'Limpiar'}
						</button>
					</div>
				</form>

				<div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
					<div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
						<div className="w-full md:w-72">
							<label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
								📅 Filtrar por Período (Año-Mes)
							</label>
							<input
								type="month"
								value={periodoSeleccionado}
								onChange={handlePeriodoChange}
								className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-purple-500 focus:ring-purple-200 transition-all bg-white hover:border-gray-300"
							/>
						</div>
						<button
							type="button"
							onClick={buscarPorPeriodo}
							disabled={loadingCartera}
							className="bg-primaryBlue text-white px-4 py-2 rounded-xl text-sm hover:opacity-90 disabled:opacity-50"
						>
							Buscar
						</button>
						<button
							type="button"
							onClick={() => {
								setPeriodoSeleccionado('');
								setErrorBusqueda('');
								setRegistros([]);
							}}
							className="border border-gray-300 text-gray-700 px-4 py-2 rounded-xl text-sm hover:bg-gray-50"
						>
							Limpiar búsqueda
						</button>
					</div>

					{errorBusqueda && <p className="text-red-600 text-sm mb-3">{errorBusqueda}</p>}
					{loadingCartera && <p className="text-sm text-gray-500 mb-3">Cargando carteras...</p>}

					<div className="overflow-x-auto rounded-xl border border-gray-100">
						<table className="min-w-full text-sm">
							<thead>
								<tr className="bg-gray-100 text-gray-700">
									<th className="px-3 py-2 text-left">#</th>
									<th className="px-3 py-2 text-left">Periodo</th>
									<th className="px-3 py-2 text-left">Cartera</th>
									<th className="px-3 py-2 text-left">Desde</th>
									<th className="px-3 py-2 text-left">Hasta</th>
									<th className="px-3 py-2 text-left">Cuota Cero</th>
									<th className="px-3 py-2 text-left">Acciones</th>
								</tr>
							</thead>
							<tbody>
								{registros.length === 0 ? (
									<tr>
										<td className="px-3 py-4 text-center text-gray-500" colSpan={7}>
											No hay datos cargados.
										</td>
									</tr>
								) : (
									registros.map((item, index) => (
										<tr key={item.idCbo_TipoCartera || `${item.Cartera}-${index}`} className="border-b border-gray-100">
											<td className="px-3 py-2">{index + 1}</td>
											<td className="px-3 py-2">{item.Periodo}</td>
											<td className="px-3 py-2 font-medium text-gray-800">{item.Cartera}</td>
											<td className="px-3 py-2">{item.Desde}</td>
											<td className="px-3 py-2">{item.Hasta}</td>
											<td className="px-3 py-2">{esVerdadero(item.Cuota_Cero) ? 'Sí' : 'No'}</td>
											<td className="px-3 py-2">
												<div className="flex items-center gap-2">
													<button
														type="button"
														onClick={() => cargarParaEditar(item)}
														className="text-blue-600 hover:text-blue-800 font-medium"
													>
														Editar
													</button>
													<button
														type="button"
														onClick={() => setConfirmDeleteId(item.sCbo_TipoCartera)}
														className="text-red-600 hover:text-red-800 font-medium"
													>
														Eliminar
													</button>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>

				{confirmDeleteId && (
					<div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
						<div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-5">
							<h4 className="text-lg font-semibold text-gray-800">Confirmar eliminación</h4>
							<p className="text-sm text-gray-600 mt-2">¿Seguro que deseas eliminar este registro de Tipo Cartera?</p>
							<div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
								<p><span className="font-semibold">Periodo:</span> {registroAEliminar?.Periodo || '-'}</p>
								<p><span className="font-semibold">Cartera:</span> {registroAEliminar?.Cartera || '-'}</p>
							</div>
							<div className="flex justify-end gap-2 mt-5">
								<button
									type="button"
									onClick={() => setConfirmDeleteId(null)}
									className="border border-gray-300 text-gray-700 px-4 py-2 rounded-xl text-sm hover:bg-gray-50"
								>
									Cancelar
								</button>
								<button
									type="button"
									onClick={confirmarEliminacion}
									className="bg-red-600 text-white px-4 py-2 rounded-xl text-sm hover:bg-red-700"
								>
									Eliminar
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
